import React from 'react';
import { Row, Col, Form, Dropdown } from 'react-bootstrap';
import { FaRuler, FaWarehouse } from 'react-icons/fa';
import { Option } from '../../../../../../../../../../types';
import './UnitsConfiguration.scss';

interface UnitsConfigurationProps {
  // Units and stock data
  unidadesMedida: Option[];
  selectedUnidadName: string;
  onUnidadMedidaSelect: (id: number, name: string) => void;
  currentStock: string;
  setCurrentStock: (value: string) => void;
  minQuantity: string;
  setMinQuantity: (value: string) => void;
  
  // Storage units
  storageUnits: any[];
  selectedStorageUnit: any;
  setSelectedStorageUnit: (unit: any) => void;
  
  // Validation props
  unidadError?: boolean;
  stockError?: boolean;
  minimoError?: boolean;
  almacenamientoError?: boolean;
  onUnidadBlur?: () => void;
  onStockBlur?: () => void;
  onMinimoBlur?: () => void;
  onAlmacenamientoBlur?: () => void;
}

const UnitsConfiguration: React.FC<UnitsConfigurationProps> = ({
  unidadesMedida,
  selectedUnidadName,
  onUnidadMedidaSelect,
  currentStock,
  setCurrentStock,
  minQuantity,
  setMinQuantity,
  storageUnits,
  selectedStorageUnit,
  setSelectedStorageUnit,
  unidadError = false,
  stockError = false,
  minimoError = false,
  almacenamientoError = false,
  onUnidadBlur,
  onStockBlur,
  onMinimoBlur,
  onAlmacenamientoBlur,
}) => {
  const handleCurrentStockChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
      setCurrentStock(value);
    }
  };

  const handleMinQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
      setMinQuantity(value);
    }
  };

  const getStorageUnitName = (unit: any) => unit?.name || unit?.storage_unit_name || 'Seleccionar';

  return (
    <div className="config-section units-section">
      <div className="section-header">
        <h6 className="section-title">Configuración de Unidades</h6>
        <p className="section-subtitle">Define la unidad de medida, cantidad y almacenamiento</p>
      </div>
      
      <Row className="g-3">
        <Col lg={4} md={6}>
          <div className="control-group">
            <div className="control-header">
              <FaRuler className="control-icon" />
              <Form.Label>Unidad *</Form.Label>
            </div>
            <Dropdown
              onSelect={(eventKey: string | null) => {
                const id = Number(eventKey);
                const unidad = unidadesMedida.find(um => um.id === id);
                if (unidad) {
                  onUnidadMedidaSelect(id, unidad.name);
                }
              }}
            >
              <Dropdown.Toggle className={`modern-dropdown-toggle ${unidadError ? 'is-invalid' : ''}`} onBlur={() => onUnidadBlur?.()}>
                {selectedUnidadName || 'Seleccionar unidad'}
              </Dropdown.Toggle>
              <Dropdown.Menu className="modern-dropdown-menu">
                {unidadesMedida.map((unidad: Option) => (
                  <Dropdown.Item key={unidad.id} eventKey={unidad.id.toString()}>
                    {unidad.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            {unidadError && <div className="invalid-feedback d-block">Este campo es requerido</div>}
          </div>
        </Col>

        <Col lg={2} md={3}>
          <div className="control-group">
            <Form.Label>Stock *</Form.Label>
            <Form.Control
              type="number"
              value={currentStock}
              placeholder="0"
              onChange={handleCurrentStockChange}
              onBlur={() => onStockBlur?.()}
              className={`modern-input compact ${stockError ? 'is-invalid' : ''}`}
              min="0"
              step="1"
            />
            {stockError && <div className="invalid-feedback d-block">Este campo es requerido</div>}
          </div>
        </Col>

        <Col lg={2} md={3}>
          <div className="control-group">
            <Form.Label>Mínimo</Form.Label>
            <Form.Control
              type="number"
              value={minQuantity}
              placeholder="0"
              onChange={handleMinQuantityChange}
              onBlur={() => onMinimoBlur?.()}
              className={`modern-input compact ${minimoError ? 'is-invalid' : ''}`}
              min="0"
              step="1"
            />
            {minimoError && <div className="invalid-feedback d-block">Este campo es requerido</div>}
          </div>
        </Col>
        
        <Col lg={4} md={6}>
          <div className="control-group">
            <div className="control-header">
              <FaWarehouse className="control-icon" />
              <Form.Label>Almacenamiento *</Form.Label>
            </div>
            <Dropdown
              onSelect={(eventKey: string | null) => {
                const unit = storageUnits.find((u: any) => u.storage_unit_id.toString() === eventKey);
                if (unit) {
                  setSelectedStorageUnit(unit.storage_unit_id);
                }
              }}
            >
              <Dropdown.Toggle className={`modern-dropdown-toggle ${almacenamientoError ? 'is-invalid' : ''}`} onBlur={() => onAlmacenamientoBlur?.()}>
                {selectedStorageUnit
                  ? getStorageUnitName(storageUnits.find((u: any) => u.storage_unit_id === (selectedStorageUnit?.storage_unit_id || selectedStorageUnit)))
                  : 'Seleccionar almacén'
                }
              </Dropdown.Toggle>
              <Dropdown.Menu className="modern-dropdown-menu">
                {storageUnits.map((unit: any) => (
                  <Dropdown.Item 
                    key={unit.storage_unit_id} 
                    eventKey={unit.storage_unit_id.toString()}
                    className={unit.is_default ? 'default-option' : ''}
                  >
                    {getStorageUnitName(unit)}
                    {unit.is_default && <span className="default-badge">Por defecto</span>}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            {almacenamientoError && <div className="invalid-feedback d-block">Este campo es requerido</div>}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default UnitsConfiguration;
